import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import { paymentReviewApi } from '../services/api';

/**
 * The ONLY post-completion refund path (booking contract §8).
 *
 * Self-service refunds do not exist: the automatic "cancel a completed booking → refund" branch
 * was removed because a repeated cancel could fire a second refund. So refunding is an audited
 * admin action, and this is its surface.
 *
 * ## The idempotency key is the point
 * `requestId` is generated ONCE per refund attempt and resent unchanged on every retry. That is
 * what makes a timeout safe: the server reserves the key before calling PayPal and sends it as
 * `PayPal-Request-Id`, so a retry returns the original refund instead of making a second one. A
 * fresh id per click would defeat the whole mechanism, which is why it is minted when the dialog
 * opens rather than when the request is sent.
 */
const AdminRefundPanel = ({ order, onRefunded }) => {
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [requestId, setRequestId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  const captured = order?.paymentStatus === 'CAPTURED';
  const capturedAmount = useMemo(
    () => order?.acceptedQuoteAmount ?? order?.price ?? null,
    [order],
  );

  if (!captured) return null;

  const openDialog = () => {
    setAmount(capturedAmount != null ? String(capturedAmount) : '');
    setReason('');
    setError(null);
    setResult(null);
    // Minted once, here — see the note above.
    setRequestId(
      (typeof crypto !== 'undefined' && crypto.randomUUID)
        ? crypto.randomUUID()
        : `refund-${order.id}-${Date.now()}-${Math.random().toString(16).slice(2)}`,
    );
    setOpen(true);
  };

  const parsed = Number(amount);
  const amountValid = Number.isFinite(parsed) && parsed > 0 &&
    (capturedAmount == null || parsed <= Number(capturedAmount));
  const canSubmit = amountValid && reason.trim().length > 0;

  const submit = async () => {
    setSaving(true);
    setError(null);
    try {
      const response = await paymentReviewApi.refund(order.id, {
        amount: parsed,
        reason: reason.trim(),
        requestId,
      });
      setResult(response);
      if (onRefunded) await onRefunded();
    } catch (e) {
      // The requestId is deliberately NOT regenerated: pressing Refund again must replay the
      // same request, not start a second one.
      setError(e?.message || 'The refund could not be completed. Retrying is safe.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Typography variant="h6" gutterBottom>Refund</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        This booking is captured, so cancelling can no longer release the money. A refund is the
        only way back, and it is audited.
      </Typography>
      <Button variant="outlined" color="error" onClick={openDialog}>Refund this booking</Button>

      <Dialog open={open} onClose={() => !saving && setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Refund booking #{order.id}</DialogTitle>
        <DialogContent>
          {result ? (
            <Alert severity={result.status === 'COMPLETED' ? 'success' : 'warning'}>
              {result.status === 'COMPLETED'
                ? `Refunded. PayPal refund id ${result.refundId}.`
                : `Refund state: ${result.status}. It will be reconciled against PayPal.`}
              {result.replayed && ' (This replayed an earlier attempt rather than refunding again.)'}
            </Alert>
          ) : (
            <>
              <DialogContentText sx={{ mb: 2 }}>
                Partial refunds are allowed up to the captured amount. The total across all
                refunds is enforced by the server.
              </DialogContentText>
              {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
              <TextField
                fullWidth
                sx={{ mb: 2 }}
                label="Amount"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                error={!!amount && !amountValid}
                helperText={
                  capturedAmount != null
                    ? `Captured: ${capturedAmount} ${order.currencyCode || ''}`
                    : 'Currency is taken from the booking.'
                }
              />
              <TextField
                fullWidth
                multiline
                minRows={2}
                label="Reason (audited, required)"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
              <Box sx={{ mt: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  Idempotency key {requestId} — reused on retry so a timeout cannot double-refund.
                </Typography>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={saving}>
            {result ? 'Close' : 'Cancel'}
          </Button>
          {!result && (
            <Button variant="contained" color="error" onClick={submit} disabled={!canSubmit || saving}>
              {saving ? <CircularProgress size={18} /> : 'Refund'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default AdminRefundPanel;
